// @givmo/board — opening a working consent, from a form or from a blank page.
//
// ══ THE BAND-2 RAILS, AS THIS DIALOG IMPLEMENTS THEM ════════════════════════════════════════════
//
// Plan §3.2 permits a form library only inside named controls, and three of them are structural in
// what this dialog does and does not do:
//
//   * ORGANIZATION-SELECTED. The officer opens a list and picks. Nothing here reads the organization's own facts
//     and proposes a form; there is no "recommended", no default selection, and no ordering derived
//     from anything but the library's own order. The blank page is offered on the same footing as the
//     forms, not as a fallback.
//   * ORGANIZATION-POPULATED. Every operative term is a `[bracketed]` field the officer types
//     (`template-fill.ts`). This app supplies no value for any of them — not even an obvious one like
//     the organization's own name, which it could look up. Filling that in would make the product the
//     author of a term in the board's instrument, which is exactly the line the rail draws.
//   * LABELLED FOR COUNSEL. `TemplateNotice` renders the backend's own review line wherever a form is
//     shown, before the officer has typed anything into it.
//
// The module never says "you need a resolution for this" — there is no surface here that could, because
// nothing about the organization's situation is an input to this dialog.
//
// ⚠ A FORM'S TEXT IS PREVIEWED, NOT EDITED, IN THIS DIALOG — and that is a deliberate ordering rather
// than a restriction. The body is DERIVED from the fields on every keystroke, so an edit made to the
// text here would be silently overwritten the moment the next field changed. A draft's text is editable
// in place (the backend refuses that only once circulated), so the honest sequence is: fill the fields,
// save the draft, then edit the text with the full editor on the draft itself. The blank page has no
// derivation to lose, so there the body is typed directly.
//
// CBG-YR6-CONSENT: "Whose written consent" — the board's, or one of its committees' that stands —
// fixed when the draft is saved (the backend refuses a change), and offered only once the board has
// a committee. A committee's consent goes to the members who vote on it when it is put to them.
//
// CBG-FIX86 (frames f1, f1b, f1c; Jonathan's Q2, 2026-10-16): WHOSE FIRST. Once the board has a
// committee, "Whose written consent" leads the dialog, and the forms beneath it are only the ones
// written for that body (`written_for`): the board's three, or a committee's two, which are fetched
// for the committee chosen and name it from the records. A board form can never reach a committee's
// consent, and the body is a settled fact once a form or the blank page is chosen. A board with no
// committee sees exactly the dialog it always had.

import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  isGivmoError,
  type BoardConsentTemplateOut,
  type OrgBoardWrittenConsentOut,
} from '@givmo/api-client';
import {
  Button,
  Card,
  CardBody,
  Dialog,
  ErrorMessage,
  Select,
  Skeleton,
  TextField,
  Textarea,
  toast,
} from '@givmo/design-system';
import { FieldNote } from '../../components/field-note';
import { CounselSentence } from '../../components/registered-disclaimer';
import { committeeMeetingsWords as cm } from '../committees/committee-meetings-words';
import { committeeOnlyWords as co } from '../committees/committee-only-words';
import { extractCommittees } from '../committees/committees-decode';
import { TemplateNotice } from './template-notice';
import { extractTemplates, todayAsDateInput } from './consents-decode';
import {
  fillTemplateBody,
  isLongFormField,
  remainingFields,
  templateFieldLabel,
  type TemplateFieldValues,
} from './template-fill';
import { useBoardOperations } from '../../board-runtime';

export interface ConsentDraftDialogProps {
  orgRef: string;
  onClose: () => void;
  onCreated: (consent: OrgBoardWrittenConsentOut) => void;
}

export function ConsentDraftDialog({ orgRef, onClose, onCreated }: ConsentDraftDialogProps) {
  const operations = useBoardOperations();
  const [template, setTemplate] = useState<BoardConsentTemplateOut | null>(null);
  const [blankPage, setBlankPage] = useState(false);
  const [title, setTitle] = useState('');
  const [consentDate, setConsentDate] = useState(() => todayAsDateInput());
  const [values, setValues] = useState<TemplateFieldValues>({});
  const [typedBody, setTypedBody] = useState('');
  const [error, setError] = useState<string | null>(null);
  // CBG-YR6-CONSENT: whose consent — a committee's id, or '' for the board.
  const [actingBody, setActingBody] = useState('');
  const committeesQuery = useQuery(
    operations.officerListBoardCommitteesOptions({ path: { org_ref: orgRef } }),
  );
  const committees = extractCommittees(committeesQuery.data).filter(
    (row) => row.status === 'active',
  );
  const committee = committees.find((row) => row.id === actingBody) ?? null;

  const templatesQuery = useQuery({
    queryKey: ['board-consent-templates', orgRef],
    queryFn: async (): Promise<BoardConsentTemplateOut[]> => {
      const { data } = await operations.officerListBoardConsentTemplates({
        path: { org_ref: orgRef },
        throwOnError: true,
      });
      return extractTemplates(data);
    },
  });

  // CBG-FIX86: the forms written for the body chosen (the library is one static read; each form says
  // whom it is written for, and a form that doesn't is the board's).
  const forms = (templatesQuery.data ?? []).filter(
    (form) => (form.written_for === 'committee') === (committee !== null),
  );

  const openForm = useMutation({
    mutationFn: async (templateId: string): Promise<BoardConsentTemplateOut> => {
      // The list projection carries the form's metadata; the full text comes from its own route — a
      // committee's form fetched for the committee chosen, whose name it then carries.
      const { data } = await operations.officerGetBoardConsentTemplate({
        path: { org_ref: orgRef, template_id: templateId },
        ...(committee !== null ? { query: { committee_id: committee.id } } : {}),
        throwOnError: true,
      });
      return data;
    },
    onSuccess: (data) => {
      setTemplate(data);
      setValues({});
      setTitle(data.name ?? '');
      setError(null);
    },
    onError: (caught) =>
      setError(isGivmoError(caught) ? caught.message : 'We couldn’t open that form just now.'),
  });

  const fields = template?.fill_in_fields ?? [];
  const body = useMemo(() => {
    if (template === null) return typedBody;
    return fillTemplateBody(template.body ?? '', fields, values);
  }, [template, fields, values, typedBody]);
  const stillToFill = useMemo(
    () => (template === null ? [] : remainingFields(body, fields)),
    [template, body, fields],
  );

  const create = useMutation({
    mutationFn: async (): Promise<OrgBoardWrittenConsentOut> => {
      const { data } = await operations.officerCreateBoardWrittenConsent({
        path: { org_ref: orgRef },
        body: {
          title: title.trim(),
          consent_date: consentDate,
          body,
          ...(committee !== null ? { committee_id: committee.id } : {}),
        },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: (consent) => {
      toast.success(
        committee !== null
          ? cm.draftSaved(committee.name)
          : 'Draft saved. Nothing has gone to the board yet.',
      );
      onCreated(consent);
    },
    onError: (caught) =>
      setError(
        isGivmoError(caught) ? caught.message : 'We couldn’t save this draft. Please try again.',
      ),
  });

  const composing = template !== null || blankPage;
  const canSave =
    composing && title.trim().length > 0 && consentDate.length > 0 && !create.isPending;

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !create.isPending && onClose()}>
      <Dialog.Content
        aria-label="Start a written consent"
        className="flex max-h-[80vh] flex-col gap-16 overflow-y-auto"
        testId="consent-draft-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Start a written consent
        </Dialog.Title>

        {!composing ? (
          <>
            <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
              {/* CBG-FIX86 (frame f1): with a committee to choose, the lead says both bodies. Removed
                  before finishing: its second sentence, which the field beneath it shows. */}
              {committees.length > 0
                ? co.pickerLead
                : 'A written consent is how a board acts without holding a meeting. Start from one of these forms, or from a blank page — whichever suits what your board is doing.'}
            </p>

            {committees.length > 0 ? (
              <div className="flex flex-col gap-6">
                <Select
                  label={cm.whoseConsent}
                  appearance="saas"
                  value={committee?.id ?? 'board'}
                  onValueChange={(next) =>
                    setActingBody(next === null || next === 'board' ? '' : next)
                  }
                  options={[
                    { value: 'board', label: cm.theBoard },
                    ...committees.map((row) => ({ value: row.id, label: row.name })),
                  ]}
                  testId="consent-draft-body-select"
                />
                {committee !== null ? (
                  <FieldNote testId="consent-draft-body-note">
                    {cm.goesToItsMembers(committee.name)}
                  </FieldNote>
                ) : null}
              </div>
            ) : null}

            {templatesQuery.isPending ? (
              <Skeleton className="h-84 w-full rounded-lg" testId="consent-templates-loading" />
            ) : null}

            {templatesQuery.isError ? (
              <ErrorMessage testId="consent-templates-error">
                We couldn’t load the forms just now. You can still start from a blank page.
              </ErrorMessage>
            ) : null}

            {forms.map((form) => (
              <Card key={form.template_id} testId={`consent-template-${form.template_id}`}>
                <CardBody className="flex flex-col gap-8">
                  <span className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
                    {form.name}
                  </span>
                  <span className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
                    {form.provenance}
                  </span>
                  <span className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary">
                    {form.review_line}
                  </span>
                  <Button
                    appearance="saas"
                    tone="secondary"
                    size="sm"
                    className="self-start"
                    loading={openForm.isPending && openForm.variables === form.template_id}
                    disabled={openForm.isPending}
                    onClick={() => openForm.mutate(form.template_id)}
                    testId={`consent-template-use-${form.template_id}`}
                  >
                    Use this form
                  </Button>
                </CardBody>
              </Card>
            ))}

            {/* D-CBG-62: Board's counsel sentence once under the list of forms, never on each card. */}
            {forms.length > 0 ? (
              <CounselSentence orgRef={orgRef} testId="consent-templates-counsel" />
            ) : null}

            <Card testId="consent-template-blank">
              <CardBody className="flex flex-col gap-8">
                <span className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
                  Start from a blank page
                </span>
                <span className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
                  {committee !== null
                    ? co.blankForCommittee
                    : 'Write the consent in your board’s own words.'}
                </span>
                <Button
                  appearance="saas"
                  tone="secondary"
                  size="sm"
                  className="self-start"
                  onClick={() => setBlankPage(true)}
                  testId="consent-template-use-blank"
                >
                  Write it yourself
                </Button>
              </CardBody>
            </Card>

            {error !== null ? (
              <ErrorMessage testId="consent-draft-error">{error}</ErrorMessage>
            ) : null}

            <div className="flex justify-end">
              <Button
                appearance="saas"
                tone="secondary"
                onClick={onClose}
                testId="consent-draft-cancel"
              >
                Cancel
              </Button>
            </div>
          </>
        ) : (
          <>
            {template !== null ? (
              <TemplateNotice
                orgRef={orgRef}
                reviewLine={template.review_line}
                provenance={template.provenance}
                sourceCitations={template.source_citations}
              />
            ) : null}

            {/* CBG-FIX86 (frame f1b): whose consent was chosen first, so here it is a settled fact,
                not a field — a different body is a different form. Removed before finishing: "It goes
                to the members who vote on …", said once already where the body was chosen. */}
            {committees.length > 0 ? (
              <div className="flex flex-col gap-6" data-testid="consent-draft-body-fact">
                <span className="font-sans text-s13 font-w600 tracking-givmo text-text-strong">
                  {cm.whoseConsent}
                </span>
                <span className="font-sans text-s15 font-w500 tracking-givmo text-text-strong">
                  {committee !== null ? committee.name : cm.theBoard}
                </span>
              </div>
            ) : null}

            <TextField
              label="Title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              testId="consent-draft-title"
            />

            <TextField
              label="Date this consent bears"
              type="date"
              value={consentDate}
              onChange={(event) => setConsentDate(event.target.value)}
              testId="consent-draft-date"
            />
            <FieldNote>
              This date is part of what a signature names, so it is set now rather than later.
            </FieldNote>

            {fields.map((field) => (
              <FormField
                key={field}
                field={field}
                value={values[field] ?? ''}
                onChange={(next) => setValues((current) => ({ ...current, [field]: next }))}
              />
            ))}

            {template !== null ? (
              <>
                {stillToFill.length > 0 ? (
                  <FieldNote testId="consent-draft-remaining">
                    {stillToFill.length} {stillToFill.length === 1 ? 'field is' : 'fields are'}{' '}
                    still in brackets below. You can save now and finish later —{' '}
                    {/* CBG-FIX90 (frame f1c): a committee's draft goes to the committee. */}
                    {committee !== null
                      ? co.nothingGoesToCommittee(committee.name)
                      : 'nothing goes to your board until you circulate it.'}
                  </FieldNote>
                ) : null}
                <div className="flex flex-col gap-6">
                  <span className="font-sans text-s13 font-w600 tracking-givmo text-text-strong">
                    What the consent will say
                  </span>
                  <pre
                    className="max-h-[280px] overflow-y-auto whitespace-pre-wrap rounded-md border border-border-subtle bg-surface-sunken p-16 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
                    data-testid="consent-draft-preview"
                  >
                    {body}
                  </pre>
                  <FieldNote>
                    Save this as a draft and you can edit the wording directly before it goes out.
                  </FieldNote>
                </div>
              </>
            ) : (
              <Textarea
                label="What the consent says"
                rows={12}
                value={typedBody}
                onChange={(event) => setTypedBody(event.target.value)}
                testId="consent-draft-body"
              />
            )}

            {error !== null ? (
              <ErrorMessage testId="consent-draft-error">{error}</ErrorMessage>
            ) : null}

            <div className="flex justify-end gap-10">
              <Button
                appearance="saas"
                tone="secondary"
                disabled={create.isPending}
                onClick={onClose}
                testId="consent-draft-cancel"
              >
                Cancel
              </Button>
              <Button
                appearance="saas"
                tone="primary"
                loading={create.isPending}
                disabled={!canSave}
                onClick={() => {
                  setError(null);
                  create.mutate();
                }}
                testId="consent-draft-save"
              >
                Save as a draft
              </Button>
            </div>
          </>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
}

function FormField({
  field,
  value,
  onChange,
}: {
  field: string;
  value: string;
  onChange: (next: string) => void;
}) {
  const label = templateFieldLabel(field);
  const testId = `consent-draft-field-${slug(field)}`;
  return isLongFormField(field) ? (
    <Textarea
      label={label}
      rows={4}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      testId={testId}
    />
  ) : (
    <TextField
      label={label}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      testId={testId}
    />
  );
}

/** A stable testid fragment for a field name — the data contract e2e selects on. */
function slug(field: string): string {
  return templateFieldLabel(field)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40);
}
